import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MatRippleModule } from '@angular/material/core';
import { MatListModule } from '@angular/material/list';
import { MatRadioModule } from '@angular/material/radio';
import {
  CurrentScopeFacade,
  PracticeSelectorComponent,
} from '@principle-theorem/ng-principle-shared';
import {
  NgDialogModule,
  TypedFormControl,
  TypedFormGroup,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import { Brand } from '@principle-theorem/principle-core';
import {
  IBrand,
  IMarketingCampaign,
  IPractice,
  MarketingCampaignType,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  filterUndefined,
  Firestore,
  getEnumValues,
  WithRef,
} from '@principle-theorem/shared';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';

const campaignTypeLabels = [
  {
    label: `General Communication (notices, recalls, re-engagement, etc.)`,
    value: MarketingCampaignType.GeneralNotice,
  },
  {
    label: `Promotional (Requires Marketing Opt-In)`,
    value: MarketingCampaignType.Marketing,
  },
];

export type IEditCampaignSettingsFormData = Pick<
  IMarketingCampaign,
  'name' | 'type' | 'practiceRef'
>;

@Component({
  selector: 'pr-campaign-settings-form',
  templateUrl: './campaign-settings-form.component.html',
  styleUrl: './campaign-settings-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgDialogModule,
    PracticeSelectorComponent,
    MatRadioModule,
    MatListModule,
    MatRippleModule,
  ],
})
export class CampaignSettingsFormComponent {
  private _scope = inject(CurrentScopeFacade);
  campaignTypes = getEnumValues(MarketingCampaignType);
  campaignTypeLabels = campaignTypeLabels;
  form = new TypedFormGroup<IEditCampaignSettingsFormData>({
    name: new TypedFormControl('', Validators.required),
    type: new TypedFormControl(
      MarketingCampaignType.GeneralNotice,
      Validators.required
    ),
    practiceRef: new TypedFormControl<DocumentReference<IPractice>>(
      undefined,
      Validators.required
    ),
  });
  hasMultiplePractices$: Observable<boolean>;
  selectedPractice$ = new ReplaySubject<WithRef<IPractice>>(1);
  brands$: Observable<WithRef<IBrand>[]>;

  practiceRef = input<DocumentReference<IPractice>>();
  formValue = input<IEditCampaignSettingsFormData | undefined>(undefined);
  formValueChange = output<IEditCampaignSettingsFormData>();

  constructor() {
    const brand$ = this._scope.currentBrand$.pipe(filterUndefined());
    this.brands$ = brand$.pipe(map((brand) => [brand]));

    this.hasMultiplePractices$ = brand$.pipe(
      switchMap((brand) => Brand.practices$(brand)),
      map((practices) => practices.length > 1)
    );

    const campaignPractice$ = toObservable(this.formValue).pipe(
      switchMap((formValue) =>
        formValue ? Firestore.getDoc(formValue.practiceRef) : of(undefined)
      ),
      take(1)
    );
    const currentPractice$ = this._scope.currentPractice$.pipe(take(1));
    combineLatest([campaignPractice$, currentPractice$])
      .pipe(
        map(
          ([campaignPractice, currentPractice]) =>
            campaignPractice ?? currentPractice
        ),
        filterUndefined(),
        take(1),
        takeUntilDestroyed()
      )
      .subscribe((practice) => this.selectedPractice$.next(practice));

    toObservable(this.formValue)
      .pipe(distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((formValue) => {
        if (formValue) {
          this.form.patchValue(formValue, { emitEvent: false });
        }
      });

    validFormGroupChanges$(this.form)
      .pipe(takeUntilDestroyed())
      .subscribe((formValue) => this.formValueChange.emit(formValue));
  }

  practiceSelected(practice: WithRef<IPractice>): void {
    this.form.patchValue({ practiceRef: practice.ref });
  }
}
